import {Component, OnInit, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {environment} from '@env/environment';
import {ProductionSiteDTO} from '@rsp/models/dto/tenants-details.dto';
import {
  CustomersEnergyDataDTO,
  CustomersEnergyDatasDTO,
  MixedBalancingType
} from '@rsp/models/dto/customers-energy-data.dto';
import {CustomersEnergyUsageDTO, CustomersEnergyUsagesDTO} from '@rsp/models/dto/customers-energy-usage.dto';
import {DateSelectionFormGroup} from '@rsp/shared/component/date-selection/date-selection.form';
import {ChartState} from '@rsp/models/chart.state';
import {StrombilanzAutarkieGradChartComponent} from '@rsp/shared/component/graph/impl/strombilanz-autarkie-grad-chart.component';
import {StrombilanzEnergieStroemeChartComponent} from '@rsp/shared/component/graph/impl/strombilanz-energie-stroeme-chart.component';
import {TenantConfigService} from '@rsp/shared/services/tenant-config.service';
import {ImageUrlBuilderService} from '@rsp/shared/services/image-url-builder.service';
import {RspEndpointsService} from '@rsp/shared/services/rest/rsp-endpoints.service';
import {IntervalEnum} from '@rsp/models/enum/interval.enum';
import {DateRangeDTO} from '@rsp/models/dto/date-range.dto';
import {getStyleColor, throttleRequest, validateChain} from '@rsp/shared/util/util';
import {DTOHasDataValidator} from '@rsp/shared/component/loading-wrapper/dto-has-data-validator';
import {BehaviorSubject} from 'rxjs';
import {UserTrackingService} from '@rsp/shared/services/user-tracking.service';

@Component({
  selector: 'rsp-strombilanz',
  templateUrl: './strombilanz.component.html',
  styleUrls: ['./strombilanz.component.css']
})

export class StrombilanzComponent implements OnInit {

  public thccolor0: string;
  public thccolor1: string;
  public thccolor2: string;
  public thccolor3: string;
  public thccolor4: string;


  strombilanzh: string;
  strombilanzt: string;


  kcme_leistung: string;

  kpst_powerplants: ProductionSiteDTO[] = [];
  showPlants: boolean;

  public bilanzierungsText: string = undefined;

  public _customerEnergyDatasDTO: CustomersEnergyDatasDTO;
  public _customerEnergyPercUsagesDTO: CustomersEnergyUsagesDTO;
  public _customerEnergyUsagesDTO: CustomersEnergyUsagesDTO;


  public dateSelectionFormGroup: DateSelectionFormGroup = new DateSelectionFormGroup();
  public chartStateCustomerData: ChartState = new ChartState();
  public chartStateCustomerAutarkie: ChartState = new ChartState();
  public showAutarkieGraph: boolean;
  public isMock: boolean;
  @ViewChild(StrombilanzEnergieStroemeChartComponent) public graphEnergieStroeme;
  @ViewChild('graphEnergieMenge') public graphEnergieMenge: StrombilanzAutarkieGradChartComponent;
  @ViewChild('graphAutarkie') public graphAutarkie: StrombilanzAutarkieGradChartComponent;
  private customerAutarkieChain = new BehaviorSubject(undefined);
  private customerDataChain = new BehaviorSubject(undefined);

  constructor(
    private titleService: Title,
    private tenant: TenantConfigService,
    public imageUrlBuilder: ImageUrlBuilderService,
    private userTracker: UserTrackingService,
    private rspEndpointsService: RspEndpointsService) {

    this.isMock = environment.mock;

    this.showAutarkieGraph = true;
    this.showPlants = true;

  }


  public reflow(): void {
    this.graphAutarkie.reflow();
    this.graphEnergieMenge.reflow();
    this.graphEnergieStroeme.reflow();
  }

  public onChangeTab(event) {
    this.userTracker.trackEvent('Diagramm', 'Tab wechsel', event.tab.textLabel);
  }


  public ngOnInit(): void {

    this.thccolor0 = getStyleColor('--graph-color0');
    this.thccolor1 = getStyleColor('--graph-color1');
    this.thccolor2 = getStyleColor('--graph-color2');
    this.thccolor3 = getStyleColor('--graph-color3');
    this.thccolor4 = getStyleColor('--graph-color4');

    if (this.tenant && this.tenant.getInfo()) {
      this.strombilanzh = this.tenant.getInfo().energyBalanceHeaderText;
      this.strombilanzt = this.tenant.getInfo().energyBalanceDetailText;

      this.titleService.setTitle(this.tenant.getId() + ' | Meine Strombilanz');
    }

    this.userTracker.trackPageView();

    this.initCustomerAutarkie();
    this.initCustomerEnergyStrom();

    this.rspEndpointsService.getTenantCommunityProductionSites().subscribe((dto) => {
      this.kpst_powerplants = dto.result;
    }, (err) => {
      console.error('Error retrieving tenant production sites');
    });

    this.rspEndpointsService.getCustomerDateRange().subscribe((dto) => {
      this.initDateSelection(dto.result.ranges);
    }, (err) => {
      console.error('Error retrieving customer date ranges');
    });
  }


  public onChangeAutarkieGrad(startDate: Date, endDate: Date, interval: IntervalEnum) {
    this.customerAutarkieChain.next({start: startDate, end: endDate, interval: interval});
  }

  public onChangeEnergyStroeme(startDate: Date, endDate: Date, interval: IntervalEnum) {
    this.customerDataChain.next({start: startDate, end: endDate, interval: interval});
  }

  private initDateSelection(dateRange: DateRangeDTO) {
    this.dateSelectionFormGroup.valueChanges.subscribe((val) => {
      this.onChangeEnergyStroeme(val.date.start, val.date.end, val.interval);
      this.onChangeAutarkieGrad(val.date.start, val.date.end, val.interval);
    });
    this.dateSelectionFormGroup.init(dateRange);
  }

  private initCustomerAutarkie() {
    this.customerAutarkieChain
      .pipe(throttleRequest((a) => this.rspEndpointsService.getCustomersEnergyUsages(a.start, a.end, a.interval),
        this.chartStateCustomerAutarkie))
      .pipe(validateChain((a) => DTOHasDataValidator.validateCustomerAutarkieDTO(a), this.chartStateCustomerAutarkie))
      .subscribe((dto) => {
        this._customerEnergyUsagesDTO = dto.result;

        this._customerEnergyPercUsagesDTO = {
          ranges: dto.result.ranges,
          values: []
        };

        this._customerEnergyPercUsagesDTO.values = dto.result.values.map(value => {
          const d = value.consumption / 100;
          const customersEnergyPercUsageDto: CustomersEnergyUsageDTO = {
            selfSupply: value.selfSupply / d,
            residualConsumption: value.residualConsumption / d,
            communityConsumption: value.communityConsumption / d,
            consumption: value.consumption,
            timestamp: value.timestamp
          };

          return customersEnergyPercUsageDto;
        });


      });
  }

  private initCustomerEnergyStrom() {
    this.customerDataChain
      .pipe(throttleRequest((a) => this.rspEndpointsService.getCustomersEnergyDatas(a.start, a.end, a.interval),
        this.chartStateCustomerData))
      .pipe(validateChain((a) => DTOHasDataValidator.validateCustomerDataDTO(a), this.chartStateCustomerData))
      .subscribe((dto) => {
        this._customerEnergyDatasDTO = dto.result;
        this._customerEnergyDatasDTO.values = this._customerEnergyDatasDTO.values.map((val) => {
          val.values = val.values.map((v) => 4 * v);
          return val;
        });

        this.setBalancingType(dto.result.values);

      });
  }

  private setBalancingType(values: CustomersEnergyDataDTO[]) {
    const aggregatedBalancingType: MixedBalancingType = values.reduce((acc: MixedBalancingType, cur) => {

      if (acc === 'RLM_AND_SLP' || cur.balancingType === 'NONE') {
        return acc;
      }

      if (cur.balancingType === 'SLP') {
        if (acc === 'NONE') {
          acc = 'SLP';
        } else if (acc === 'RLM') {
          acc = 'RLM_AND_SLP';
        }
      } else if (cur.balancingType === 'RLM') {
        if (acc === 'NONE') {
          acc = 'RLM';
        } else if (acc === 'SLP') {
          acc = 'RLM_AND_SLP';
        }
      }

      return acc;
    }, 'NONE');

    switch (aggregatedBalancingType) {
      case 'NONE':
        this.bilanzierungsText = undefined;
        break;

      case 'RLM':
        this.bilanzierungsText = this.tenant.getInfo().rlmText;
        break;

      case 'SLP':
        this.bilanzierungsText = this.tenant.getInfo().slpText;
        break;

      case 'RLM_AND_SLP':
        this.bilanzierungsText = this.tenant.getInfo().rlmAndSlpText;
        break;
    }
  }
}
